/**
 * How a permit status looks, in one place.
 *
 * Used by the status pill on the detail and queue screens and by the permits list,
 * so a status cannot end up green in one view and grey in another.
 *
 * The palette carries meaning rather than just being nine different colours:
 * a SOLID pill is a state someone has to act on - money owed, or a permit taken
 * away - and everything else is a subtle tint saying where the application sits.
 * The subtle/emphasis variants are theme-aware, so they hold up in dark mode
 * without a second palette.
 */
const STATUS_CLASSES: Record<string, string> = {
    // Waiting on TLC.
    submitted: 'bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle',
    in_review: 'bg-info-subtle text-info-emphasis border border-info-subtle',
    // Blue, with in_review. Not primary-subtle: the theme's primary is an orange
    // (#FF6633), so approved rendered as a pale orange pill between amber "needs info"
    // and solid amber "pending payment" - a positive outcome wearing the colour this
    // palette uses for things that need chasing. Not green either, which put it a shade
    // away from `active` and made two adjacent, near-synonymous states look alike.
    // Blue says "still with TLC", which is what approved-not-yet-issued means.
    approved: 'bg-info-subtle text-info-emphasis border border-info-subtle',

    // Waiting on the applicant - the amber pair.
    needs_info: 'bg-warning-subtle text-warning-emphasis border border-warning-subtle',
    // Solid: an unpaid permit is the one staff chase, and it should not read as
    // just another shade of amber next to "needs info".
    pending_payment: 'text-bg-warning border border-warning',

    // Settled.
    active: 'bg-success-subtle text-success-emphasis border border-success-subtle',
    expired: 'bg-body-secondary text-body-secondary border',
    denied: 'bg-danger-subtle text-danger-emphasis border border-danger-subtle',
    // Solid: revoked is a permit taken back after it was issued, which is a heavier
    // thing than an application turned down, and the list should say so.
    revoked: 'text-bg-danger border border-danger',
};

/**
 * Issued, but not in force yet.
 *
 * Not a status - a permit is `active` from the moment it is approved, whatever its
 * start date - so this is a variant rather than another entry above. Outlined instead
 * of filled, which is the whole point of it: same green as active because nothing is
 * wrong, hollow because it is not in force. Fill = current, outline = not yet.
 */
const UPCOMING = 'text-success-emphasis border border-success';

const FALLBACK = 'bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle';

/** Bootstrap classes for a status pill, minus the badge/pill classes themselves. */
export function permitStatusClass(status?: string | null, upcoming = false): string {
    if (upcoming) {
        return UPCOMING;
    }

    return STATUS_CLASSES[status ?? ''] ?? FALLBACK;
}

/** "pending_payment" -> "pending payment"; capitalisation is left to CSS. */
export function permitStatusLabel(status?: string | null, upcoming = false): string {
    if (upcoming) {
        return 'upcoming';
    }

    return (status ?? '').replace(/_/g, ' ');
}

/**
 * The pill as markup, for the permits list.
 *
 * The CRUD table renders a custom column through [innerHTML], so this returns a
 * string rather than a component. Nothing here is user-supplied - the status comes
 * from a fixed set on the entity - and anything unrecognised falls back to the
 * neutral pill rather than being written into the page.
 */
export function permitStatusPill(value: unknown): string {
    // Takes the ROW, not the status, because the pill now depends on two fields: the
    // status for its colour and `upcoming` for whether the permit is in force. The CRUD
    // hands a custom column the whole row when its `value` is empty, which is how this
    // is configured. A bare string still works - the detail badge passes one.
    const row = (typeof value === 'object' && value !== null ? value : {}) as { status?: string; upcoming?: boolean };
    const status = typeof value === 'string' ? value : row.status;
    const upcoming = row.upcoming === true;

    const known = Object.prototype.hasOwnProperty.call(STATUS_CLASSES, status ?? '');
    const label = upcoming ? 'upcoming' : known ? permitStatusLabel(status) : 'unknown';

    return `<span class="badge rounded-pill text-capitalize ${permitStatusClass(status, upcoming)}">${label}</span>`;
}
